import { useState } from 'react';

export function QuickHelp({ title, tips, variant = 'default' }) {
  const [isOpen, setIsOpen] = useState(false);

  // Variant styles for different backgrounds
  const buttonStyles = variant === 'light' 
    ? 'text-white/80 hover:text-white' 
    : 'text-gray-500 hover:text-blue-600';
  
  const iconStyles = variant === 'light'
    ? 'bg-white/20 text-white'
    : 'bg-blue-100 text-blue-600';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 text-sm transition-colors ${buttonStyles}`}
        data-testid="quick-help-btn"
      >
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${iconStyles}`}>?</span>
        <span>How it works</span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 z-50 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>
          <ul className="space-y-2">
            {tips.map((tip, idx) => (
              <li key={idx} className="flex gap-2 text-sm text-gray-600">
                <span className="text-blue-500 font-bold">{idx + 1}.</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function EmptyState({ icon, title, description, actionText, onAction }) {
  return (
    <div className="text-center py-12 px-6 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
      <div className="text-5xl mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">{description}</p>
      {actionText && onAction && (
        <button
          onClick={onAction}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
        >
          {actionText}
        </button>
      )}
    </div>
  );
}

export function FeatureCard({ icon, title, description, onClick, highlight }) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-xl border-2 transition-all hover:shadow-md ${
        highlight 
          ? 'border-blue-500 bg-blue-50 hover:bg-blue-100' 
          : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      <div className="text-3xl mb-2">{icon}</div>
      <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
}

export const PAGE_TIPS = {
  dashboard: [
    "Each card represents one pay period",
    "See your income minus bills = leftover amount",
    "Use 'Quick Expenses' to track small spending",
    "The 'Action Engine' helps you decide where to put extra money",
    "Click 'Attack Debt', 'Build Safety Net', or 'Invest' to allocate funds"
  ],
  bills: [
    "Add all your recurring monthly bills here",
    "Set the due day so bills show in the right pay period",
    "Enable reminders to get email notifications before due dates",
    "Categories help organize your spending",
    "Bills automatically appear on your Dashboard"
  ],
  income: [
    "Add each source of income (paycheck, side gig, etc.)",
    "Set the pay date to see income in the right period",
    "Create recurring templates for regular paychecks",
    "Income is used to calculate your leftover amount",
    "Multiple income sources are summed together"
  ],
  goals: [
    "Create goals for things you're saving toward",
    "Set a target amount and optional target date",
    "Track progress as you allocate money from the Dashboard",
    "Goals can be for emergencies, vacations, or big purchases",
    "Mark goals complete when you reach them"
  ],
  debtSnowball: [
    "Add all your debts with their balances and interest rates",
    "Choose a strategy: Avalanche (highest interest first) or Snowball (smallest balance first)",
    "Enter any extra monthly payment you can make",
    "See your projected payoff date and total interest",
    "Payments from the Dashboard 'Attack Debt' feature are tracked here"
  ],
  envelopeChallenge: [
    "Set a savings goal and number of days (envelopes)",
    "Each envelope has a random amount that totals your goal",
    "Click 'Spin' daily to select an envelope",
    "Save that amount and mark it complete",
    "Use 'Respin' once per week if the amount is too hard"
  ]
};
